import Api from '@/api'
import Summary from '@/utilities/bid/fetches/Summary'
import JsonToCsv from '@/utilities/helper/JsonToCsv'
import FileManager from '@/utilities/helper/FileManager'
import DocumentTypeEnum from '@/api/enums/DocumentTypeEnum'
import Xml from '@/utilities/xml/Xml'
import { saveAs } from 'file-saver'

const summaryFetcher = new Summary()
const api = new Api()
const apiSpec = api
  .sales()
  .bid()
  .spec()

export default {
  // GETs ===================================================
  async fetchSummary({ commit, state }, { bidNumber = '', type = '', merge = false, ...queryParams }) {
    const summary = await summaryFetcher.fetch(commit, { bidNumber, type, ...queryParams })
    const models = !Array.isArray(summary) && 'models' in summary ? summary.models : summary

    if (merge) {
      state.summary.forEach(item => {
        if (!models.some(({ bidNumber }) => bidNumber === item.bidNumber)) {
          models.push(item)
        }
      })
    }

    commit('setSummary', models)

    return summary
  },
  async fetchBidSummary({ commit }, { bidNumber = '', type = '', ...queryParams }) {
    return await summaryFetcher.fetch(commit, { bidNumber, type, ...queryParams })
  },
  async fetchToPageRegister({ commit }) {
    try {
      const resp = await api
        .sales()
        .bid()
        .registerSettings()
      commit('setPageRegister', resp.data.data)
      commit('setSalesManagers', resp.data.data.salesManagers)
      commit('setCustomers', resp.data.data.customers)
      return resp
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  // Decision Approval =========================================
  async fetchDecisionSet({ commit }, bidNumber) {
    try {
      const resp = await api
        .sales()
        .decision()
        .decisionSet(bidNumber)
      commit('setDecisionSet', resp.data.data)
      return resp
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  // Specifications
  async fetchSpecifications({ commit, state }, payload) {
    try {
      const resp = await apiSpec.detail(payload)
      commit('setSpecifications', resp.data.data ?? [])
      commit('setBidNumber', resp.data.data.bid.number ?? state.bidNumber)
      return resp
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  // Specifications
  async fetchAuthority({ commit }, payload) {
    try {
      const resp = await api
        .sales()
        .bid()
        .authority(payload)
      commit('setAuthority', resp.data.data ?? [])
      return resp
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  // Download File
  async exportDocument({ commit }, payload) {
    try {
      let content
      const id = payload.bidNumber ?? payload.year ?? null
      const type = payload.type ?? DocumentTypeEnum.csv
      const filename = `${id}_${payload.service}`

      const resp = await apiSpec.exportDocument(id, payload.service, type)

      switch (type) {
        case DocumentTypeEnum.csv:
          content = new JsonToCsv(resp.data.data).fetch()
          break
        case DocumentTypeEnum.kml:
          content = new Xml(document, { namespace: resp.data.data.kml._xmlns }).parseFromJson({ kml: resp.data.data.kml })
          break
      }

      if (content) {
        saveAs(new Blob([content]), `${filename}.${type}`)
      }

      return resp
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  async addNewBid({ commit }, payload) {
    try {
      const resp = await api
        .sales()
        .bid()
        .add(payload)
      commit('setBidNumber', resp.data.data.bidNumber)
      commit('setCustomer', resp.data.data.customer)
      commit('setBidDecisionSettings', resp.data.data.decision.settings)
      commit('setBidDecisionCriteriaSets', resp.data.data.decision.calculation)
      commit('Dashboard/setFeedback', resp.data, { root: true })
      return resp
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  async addComment({ commit, state }, payload) {
    try {
      const resp = await api
        .sales()
        .comment(state.bidNumber)
        .add(payload)
      commit('setComments', [resp.data.data.comments, ...state.specifications.comments])
      commit('Dashboard/setFeedback', resp.data, { root: true })
      return resp
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  async updateComment({ commit, state }, payload) {
    try {
      const resp = await api
        .sales()
        .comment(state.bidNumber)
        .update(payload.id, payload)

      const comments = state.specifications.comments.map(comment => (comment.id === payload.id ? resp.data.data.comments : comment))

      commit('setComments', comments)
      commit('Dashboard/setFeedback', resp.data, { root: true })
      return resp
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  async deleteComment({ commit, state }, payload) {
    try {
      const resp = await api
        .sales()
        .comment(state.bidNumber)
        .delete(payload.id)

      const comments = state.specifications.comments.filter(comment => comment.id !== payload.id)

      commit('setComments', comments)
      commit('Dashboard/setFeedback', resp.data, { root: true })
      return resp
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  async changeStatus({ commit }, payload) {
    try {
      const resp = await api.sales().changeStatus(payload)
      const response = resp.data.data
      commit('Dashboard/setFeedback', response, { root: true })
      return response.status
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  async addSite({ commit, state }, payload) {
    try {
      const resp = await apiSpec.sites(state.bidNumber).add(payload)
      commit('Bid/Site/add', resp.data.data, { root: true })
      commit('Dashboard/setFeedback', resp.data, { root: true })
      return resp
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  async updateSite({ commit, state }, payload) {
    try {
      const resp = await apiSpec.sites(state.bidNumber).update(payload.id, payload)
      // change site after updating
      commit('Bid/setSiteForUpdate', resp.data.data, { root: true })
      commit('Bid/Site/update', resp.data.data, { root: true })
      commit('Dashboard/setFeedback', resp.data, { root: true })
      return resp
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  async deleteSite({ commit, state }, { id }) {
    try {
      const resp = await apiSpec.sites(state.bidNumber).delete(id)
      commit('Bid/Site/remove', id, { root: true })
      commit('Dashboard/setFeedback', resp.data, { root: true })
      return resp
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  async addArms({ commit, state }, payload) {
    try {
      const resp = await apiSpec.arms(state.bidNumber, payload.id).add(payload)
      const response = resp.data.data
      commit('setArms', response.arms)
      commit('Dashboard/setFeedback', response, { root: true })
      return resp
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  async deleteArm({ commit, state }, { id, arm }) {
    try {
      const resp = await apiSpec.arms(state.bidNumber, id).delete(arm)
      commit('Dashboard/setFeedback', resp.data.data, { root: true })
      return resp
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  // Site Ref
  async askReference({ commit, state }, payload) {
    try {
      const resp = await apiSpec.askReference(state.bidNumber, payload)
      const response = resp.data.data
      commit('Dashboard/setFeedback', response, { root: true })
      return response
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  // Attachment of Bid
  async fetchAttachment({ commit }, { bidNumber, id }) {
    try {
      const resp = await api
        .sales()
        .attachment(bidNumber)
        .fetch(id)
      const response = resp.data.data

      const fileManager = new FileManager(response?.attachments?.file, response?.attachments?.name, response?.attachments?.extension)
      fileManager.download(`attachment-${bidNumber}`)

      commit('Dashboard/setFeedback', response, { root: true })
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  async addAttachment({ commit }, { bidNumber, form }) {
    try {
      const resp = await api
        .sales()
        .attachment(bidNumber)
        .add(form)
      const response = resp.data.data
      commit('Bid/addAttachment', response?.attachments, { root: true })
      commit('Dashboard/setFeedback', response, { root: true })
      return response
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  async deleteAttachment({ commit }, { bidNumber, id }) {
    try {
      const resp = await api
        .sales()
        .attachment(bidNumber)
        .delete(id)
      const response = resp.data.data
      commit('Bid/removeAttachment', response?.attachments, { root: true })
      commit('Dashboard/setFeedback', response, { root: true })
      return response
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
  // Cloning Sites
  async cloneSites({ commit }, payload) {
    try {
      const response = await api.sales().cloneSites(payload)

      if ('specification' in response.data.data) {
        // Update Specifications if Sites were cloned in same Project
        commit('setSpecifications', response.data.data.specification)
      }

      commit('Dashboard/setFeedback', response.data.data, { root: true })

      return response.data.data
    } catch (err) {
      commit(
        'Dashboard/setFeedback',
        {
          message: err,
          errors: 500,
        },
        { root: true },
      )
    }
  },
}
